import { NextPage } from 'next'
import Head from 'next/head'
import { useCallback, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSWRConfig } from 'swr'
import { useWeb3React } from '@web3-react/core'
import { useUpdaterChainId } from '~/hooks'
import { LonStakingChart, LonCirculationChart } from '~/components/charts'
import FoldText from '~/components/FoldText'
import Footer from '~/components/Footer'
import FullPageModal from '~/components/FullPageModal'
import Header from '~/components/Header'
import { useModalOpened, useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { useIsDisableTrade } from '~/state/dex/hooks'
import {
  LonDeposit,
  LonMarket,
  PersonalAsset,
  StakeRecordList,
  UnlockDeposit,
} from './components'
import {
  StyledLonPageHelper,
  StyledStakingContainer,
  StyledStakingWrap,
} from './styled'
import { StyledTitleIcon } from '~/components/TradeMining/styled'
import IconStakeMiningLight from '~/assets/images/stake-mining-light.svg'

const Staking: NextPage = () => {
  const { t } = useTranslation()
  const { account } = useWeb3React()
  const chainId = useUpdaterChainId()

  const [page, setPage] = useState(0) // staking list page index
  const isOpenStaking = useModalOpened(ApplicationModal.LON_STAKING)
  const toggleModalStaking = useModalToggle(ApplicationModal.LON_STAKING)

  const isOpenRedemption = useModalOpened(ApplicationModal.LON_REDEMPTION)
  const toggleModalRedemption = useModalToggle(ApplicationModal.LON_REDEMPTION)

  const isDisableTrade = useIsDisableTrade()
  const toggleIpBlockedModal = useModalToggle(ApplicationModal.IP_BLOCKED)

  const { mutate } = useSWRConfig()
  const onComplete = useCallback(() => {
    mutate(['getLonBalance', chainId, account])
    mutate(['getStakingCoolDown', chainId, account])
    mutate(['getStakingCoolDownRemainSeconds', chainId, account])
    mutate(['getXLonBalance', chainId, account])
    mutate(['getStakingList', chainId, account, page])
    mutate(['getStakingListTotal', chainId, account])
  }, [account, chainId])

  const onCloseStaking = () => {
    onComplete()
    if (isOpenStaking) toggleModalStaking()
  }
  const onCloseRedemption = () => {
    onComplete()
    if (isOpenRedemption) toggleModalRedemption()
  }

  useEffect(() => {
    if (isDisableTrade) {
      toggleIpBlockedModal()
    }
  }, [isDisableTrade])

  return (
    <StyledLonPageHelper>
      <Head>
        <title>{t('meta_lon_staking_title')}</title>
        <meta name="description" content={t('meta_lon_staking_description')} />
        <meta name="keywords" content={t('meta_lon_keywords')} />
      </Head>
      <Header />
      <StyledStakingWrap>
        <StyledStakingContainer>
          <section className="lon-stake-head">
            <div className="title-area">
              <StyledTitleIcon src={IconStakeMiningLight} />
              <h1 className="title">{t('staking_title')}</h1>
            </div>
            <p className="description">
              <FoldText texts={[t('staking_subtitle')]} />
            </p>
          </section>
          <LonMarket />
          <div className="stake-charts-container">
            <LonStakingChart />
            <LonCirculationChart />
          </div>
          <PersonalAsset />
          <FullPageModal onClose={onCloseStaking} visible={isOpenStaking}>
            <LonDeposit onComplete={onComplete} />
          </FullPageModal>
          <FullPageModal onClose={onCloseRedemption} visible={isOpenRedemption}>
            <UnlockDeposit
              onComplete={onComplete}
              onClose={onCloseRedemption}
            />
          </FullPageModal>
          <StakeRecordList page={page} setPage={setPage} />
        </StyledStakingContainer>
      </StyledStakingWrap>
      <Footer />
    </StyledLonPageHelper>
  )
}

export default Staking
